import React, { useMemo, useState } from 'react';
import { Button, Drawer } from 'antd';
import { BookOpen, ChevronRight, MessageCircle, Send, X } from 'lucide-react';
import { useAppTranslation } from 'src/i18n/hooks';
import { useHistory } from 'react-router';
import { cn } from '../ui/lib/utils';
import useWindowSize from 'src/hooks/useWindowSize';

type Faq = {
  question: string;
  answer: string;
  lists?: string[];
  id: string;
};

export default function SupportWidget() {
  const { t } = useAppTranslation('support');
  const [open, setOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const { isMobile } = useWindowSize();
  const history = useHistory();

  const toggleFaq = (id: string) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  const faqs: Faq[] = useMemo(
    () => [
      {
        id: 'deposit',
        question: t('howToMakeDeposit'),
        answer: t('clickDepositAnswer'),
      },
      {
        id: 'withdrawal',
        question: t('withDrawalsRequire'),
        answer: t('withDrawalsRequireAnswer'),
      },
      {
        id: 'withdraw-funds',
        question: t('howWithDrawFunds'),
        answer: t('howWithDrawFundsAnswer'),
      },
      {
        id: 'updates',
        question: t('howUpdatedEventsActivities'),
        answer: t('howUpdatedEventsActivitiesHeader'),
        lists: [
          t('howUpdatedEventsActivitiesAnswer1'),
          t('howUpdatedEventsActivitiesAnswer2'),
          t('howUpdatedEventsActivitiesAnswer3'),
        ],
      },
    ],
    [t],
  );

  const isTradePage = history.location.pathname.includes('/trade');

  const widgetBody = (
    <>
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-white/5 px-6 py-5">
        <div className="flex items-center justify-between">
          <div className="text-lg font-medium tracking-tight text-gray-900 dark:text-white/95">
            {t('supportTitle')}
          </div>
          <button
            onClick={() => setOpen(false)}
            className="rounded-md p-1.5 text-gray-400 dark:text-white/40 transition-colors hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-600 dark:hover:text-white/70"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="space-y-6 px-6 py-6 overflow-y-auto">
        {/* Intro */}
        <div className="space-y-3">
          <h3 className="text-base font-medium leading-tight text-gray-900 dark:text-white/90">
            {t('needSomeHelp')}
          </h3>
          <p className="text-[13px] leading-relaxed text-gray-600 dark:text-white/50">
            {t('supportContent')}
          </p>
        </div>

        {/* Docs button */}
        <Button type="link" target='_blank' href='https://docs.vdex.trade/trading/onboarding' className="flex items-center h-10 w-full justify-center gap-2.5 rounded-lg border border-blue-500/20 bg-blue-500/10 font-medium text-blue-600 dark:text-blue-400 shadow-sm backdrop-blur-sm transition-all hover:border-blue-500/30 hover:bg-blue-500/15">
          <BookOpen className="size-4" />
          {t('viewOnboardingDocs')}
        </Button>

        {/* Social Buttons */}
        <div className="grid gap-2.5">
          <Button type="link" target='_blank' href='https://t.me/VDEX_Community' className="h-10 justify-center gap-2.5 flex items-center rounded-lg border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 font-medium text-gray-800 dark:text-white/90 shadow-sm transition-all hover:border-gray-300 dark:hover:border-white/15 hover:bg-gray-200 dark:hover:bg-white/10">
            <Send className="size-4" />
            {t('joinVDEXTelegram')}
          </Button>
          <Button type="link" target='_blank' href='https://discord.gg/vdex-trade' className="h-10 justify-center gap-2.5 flex items-center rounded-lg border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 font-medium text-gray-800 dark:text-white/90 shadow-sm transition-all hover:border-gray-300 dark:hover:border-white/15 hover:bg-gray-200 dark:hover:bg-white/10">
            <MessageCircle className="size-4" />
            {t('joinVDEXDiscord')}
          </Button>
        </div>

        {/* FAQ Section */}
        <div className="space-y-4">
          <p className="text-[13px] font-medium text-gray-700 dark:text-white/60">
            {t('checkoutFAQs')}
          </p>

          <div className="space-y-1">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="overflow-hidden rounded-lg border border-transparent transition-colors"
              >
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className={cn(
                    'group flex w-full items-center justify-between px-4 py-3 text-left transition-all',
                    'hover:bg-gray-100 dark:hover:bg-white/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-200 dark:focus-visible:ring-white/20',
                    expandedFaq === faq.id && 'bg-gray-50 dark:bg-white/5',
                  )}
                >
                  <span className="text-[13px] leading-snug text-gray-800 dark:text-white/70 group-hover:text-gray-900 dark:group-hover:text-white/90">
                    {faq.question}
                  </span>
                  <ChevronRight
                    className={cn(
                      'size-4 shrink-0 text-gray-400 dark:text-white/30 transition-all group-hover:text-gray-600 dark:group-hover:text-white/50',
                      expandedFaq === faq.id ? 'rotate-90' : 'group-hover:translate-x-0.5',
                    )}
                  />
                </button>
                {expandedFaq === faq.id && (
                  <div className="border-t border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-white/[0.02] px-4 py-3 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div
                      className="text-[13px] leading-relaxed text-gray-700 dark:text-white/60"
                      dangerouslySetInnerHTML={{
                        __html: faq.answer,
                      }}
                    />
                    {faq.lists && faq.lists.length > 0 && (
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-[13px] text-gray-600 dark:text-white/50">
                        {faq.lists.map((item, i) => (
                          <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {!open && (
        <button
          className={cn(
            `group fixed ${isTradePage ? 'bottom-[50px]' : 'bottom-[10px]'} tablet:bottom-5 right-5 z-50
flex items-center gap-2 font-bold rounded-lg px-3 py-2 shadow-lg transition-all duration-300 border-[1px] animate-widget-show-grow
active:scale-95`,
            'bg-[#111213] text-white',
            'dark:bg-[#111213] dark:text-white dark:border-white/30',
          )}
          onClick={() => setOpen(true)}
        >
          <MessageCircle className="size-5 shrink-0" />
          <span className="max-w-0 overflow-hidden group-hover:max-w-[100px]  transition-all duration-300 whitespace-nowrap">
            {t('supportBtn')}
          </span>
        </button>
      )}
      {/* Main Widget Panel */}
      {isMobile ? (
        <>
          <Drawer
            open={open}
            onClose={() => setOpen(false)}
            placement="bottom"
            height="100dvh"
            className="[&_.ant-drawer-content-wrapper]:!fixed [&_.ant-drawer-content-wrapper]:!inset-0 [&_.ant-drawer-body]:!p-0"
            closable={false}
          >
            {widgetBody}
          </Drawer>
        </>
      ) : (
        <>
          {/* Floating Button */}
          <div
            className={cn(
              `fixed right-0 w-full sm2:w-[450px] p-0 rounded-2xl flex flex-col z-[1001] border shadow-2xl shadow-black/50 transform transition-transform duration-300 ease-out`,
              isTradePage ? 'bottom-[50px]' : 'bottom-[0px]',
              open
                ? isTradePage
                  ? 'translate-y-[47px]'
                  : 'translate-y-[0px]'
                : 'translate-y-[20000px]',
              // Light / dark mode background + border
              'bg-white dark:bg-[#0a0b0d] border-gray-200 dark:border-white/20',
              'h-[70vh] lg:h-[80vh]',
            )}
          >
            {widgetBody}
          </div>
        </>
      )}
    </>
  );
}
